import { useNavigate } from 'react-router-dom';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import api from '@app/services/api';
import { isSupabaseConfigured, getSupabaseClient } from '@platform/auth-supabase/frontend/supabaseClient';
import { BRAND_SUBTITLE, ACCENT_RGB } from '@client/config/client.config';
import { effectiveAccentHex, hexToRgb } from '@platform/app-shell/frontend/accent';
import { HOME_URL, takeLandingAfterSignIn } from '@platform/app-shell/frontend/utils/landing';

// Resolve a step-up returnUrl to a SAFE same-origin path. A naive
// `startsWith('/') && !startsWith('//')` check is bypassable: browsers
// treat backslashes as slashes for special schemes and strip tab/CR/LF
// before parsing, so '/\evil.com' and '/%0A/evil.com' both resolve to
// the protocol-relative '//evil.com' → https://evil.com. Parsing against
// our own origin and rejecting anything that lands off-origin closes
// that (an off-origin result — including the backslash/control-char
// tricks — falls back to '/'). Returns pathname+search+hash only.
function safeReturnUrl(ret) {
  if (!ret) return '/';
  try {
    const u = new URL(ret, window.location.origin);
    if (u.origin !== window.location.origin) return '/';
    const path = (u.pathname + u.search + u.hash) || '/';
    // Even a SAME-ORIGIN parse can yield a pathname beginning with '//'
    // (e.g. '/..//evil.com' → pathname '//evil.com'); assigning that to
    // window.location.href is a protocol-relative navigation → off-origin.
    // No legitimate path starts with '//', so reject it.
    if (path.startsWith('//')) return '/';
    return path;
  } catch (_) {
    return '/';
  }
}

// The circuit background paints in the accent, so it follows the same
// precedence as the CSS tokens: this person's pick, then the site's own
// colour, then the bundled default. Before this it only knew the personal
// pick, so a browser that had never chosen one drew the build's colour on
// a sign-in page wearing the client's.
function getAccentRGB() {
  return hexToRgb(effectiveAccentHex()) || ACCENT_RGB;
}
import SignInLogo from '@platform/auth/frontend/components/SignInLogo';
import { generateNetwork, stepParticle } from '@platform/auth/frontend/circuitNetwork';
import '@platform/auth/frontend/pages/Home.css';

/* ─── Circuit Board Background ─── */
// The network itself (nodes, traces, particles) is circuitNetwork.js — pure,
// and safe for a window too small to hold a single trace.
function CircuitBackground() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const networkRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let dpr = window.devicePixelRatio || 1;

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      networkRef.current = generateNetwork(w, h);
    };

    const onMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouse);

    const render = (time) => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const net = networkRef.current;
      const accent = getAccentRGB();
      if (!net) { animRef.current = requestAnimationFrame(render); return; }
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const t = time * 0.001;

      ctx.clearRect(0, 0, w, h);

      // Draw traces
      for (const tr of net.traces) {
        // Check proximity of midpoint to mouse for tinting
        const tmx = (tr.ax + tr.bx) / 2;
        const tmy = (tr.ay + tr.by) / 2;
        const md = Math.sqrt((tmx - mx) ** 2 + (tmy - my) ** 2);
        const prox = Math.max(0, 1 - md / 150);

        if (prox > 0) {
          const alpha = 0.06 + prox * 0.18;
          const r = Math.round(40 + prox * (accent.r - 40));
          const g = Math.round(40 + prox * (accent.g - 40));
          const b = Math.round(40 + prox * (accent.b - 40));
          ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
          ctx.lineWidth = 0.8 + prox * 0.6;
        } else {
          ctx.strokeStyle = 'rgba(255,255,255,0.06)';
          ctx.lineWidth = 0.8;
        }

        ctx.beginPath();
        ctx.moveTo(tr.ax, tr.ay);
        ctx.lineTo(tr.bx, tr.by);
        ctx.stroke();
      }

      // Draw nodes
      for (const node of net.nodes) {
        const nd = Math.sqrt((node.x - mx) ** 2 + (node.y - my) ** 2);
        const prox = Math.max(0, 1 - nd / 150);
        const pulse = 0.08 + 0.12 * (0.5 + 0.5 * Math.sin(t * 1.5 + node.phase));
        const alpha = pulse + prox * 0.6;
        const radius = node.r + prox * 3;

        if (prox > 0) {
          // Accent glow
          ctx.shadowColor = `rgba(${accent.r},${accent.g},${accent.b},${prox * 0.7})`;
          ctx.shadowBlur = 8 + prox * 12;
          const r = Math.round(180 + prox * (accent.r - 180));
          const g = Math.round(180 + prox * (accent.g - 180));
          const b = Math.round(180 + prox * (accent.b - 180));
          ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        } else {
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        }

        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fill();

        // Double ring for some nodes
        if (node.ring) {
          ctx.strokeStyle = ctx.fillStyle;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius + 4, 0, Math.PI * 2);
          ctx.stroke();
        }

        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      // Draw data-flow particles (none, when the window holds no trace)
      for (const p of net.particles) {
        const at = stepParticle(p, net.traces);
        if (!at) continue;
        const px = at.x;
        const py = at.y;

        ctx.shadowColor = `rgba(${accent.r},${accent.g},${accent.b},0.6)`;
        ctx.shadowBlur = 6;
        ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},0.8)`;
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      animRef.current = requestAnimationFrame(render);
    };

    animRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className="circuit-canvas" />;
}

/* ─── Home Page ─── */
function Home() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  // Local form state for the email + password sign-in flow.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  // A PORTAL CUSTOMER'S FIRST SIGN-IN (owner, 2026-10-14). They are sent the
  // portal's ordinary address; here they type their email, and if it is a
  // portal customer who has never set a password, the password box gives
  // way to a code emailed to them, then a new password, then the portal.
  // The server decides who qualifies (auth-supabase/server/first-signin.js)
  // — staff and anyone with a password never see any of this.
  //   first      the typed email is a first sign-in (asked as they type)
  //   firstStep  null | 'code' | 'password'
  const [first, setFirst] = useState(false);
  const [firstStep, setFirstStep] = useState(null);
  const [firstCode, setFirstCode] = useState('');
  const [firstNote, setFirstNote] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newConfirm, setNewConfirm] = useState('');
  const firstSessionRef = useRef(null);
  useEffect(() => {
    if (!isSupabaseConfigured()) return undefined;
    const typed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) { setFirst(false); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.firstSignInCheck(typed).then((r) => { if (live) setFirst(!!r?.first); }).catch(() => { if (live) setFirst(false); });
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [email]);

  const sendFirstCode = async (again = false) => {
    setError(null); setFirstNote(null); setSubmitting(true);
    try {
      const r = await api.firstSignInStart(email.trim());
      if (r?.state !== 'code') { setFirst(false); setFirstStep(null); setError('Enter your password to sign in.'); return; }
      setFirstStep('code');
      if (again) setFirstNote(r.sent ? 'A new code is on its way.' : 'A code was sent less than a minute ago — check your inbox, or try again in a minute.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'The code could not be sent.');
    } finally { setSubmitting(false); }
  };

  const verifyFirstCode = async (e) => {
    e.preventDefault();
    setError(null); setFirstNote(null);
    const code = firstCode.replace(/\s+/g, '');
    if (!/^\d{6,10}$/.test(code)) { setError('Type the code from the email — numbers only.'); return; }
    setSubmitting(true);
    try {
      firstSessionRef.current = await api.firstSignInVerify(email.trim(), code);
      setFirstStep('password');
    } catch (err) {
      if (err?.response?.data?.state === 'password') { setFirst(false); setFirstStep(null); }
      setError(err?.response?.data?.error || err?.message || 'That code did not work.');
    } finally { setSubmitting(false); }
  };

  const saveFirstPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== newConfirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      // Someone else's session on this browser must never be the one the
      // password lands on.
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      const { error: sErr } = await supabase.auth.setSession(firstSessionRef.current);
      if (sErr) throw sErr;
      const { error: uErr } = await supabase.auth.updateUser({ password: newPassword });
      if (uErr) throw uErr;
      firstSessionRef.current = null;
      // The ordinary landing from here: the app token, 2FA where required,
      // and the link they came for — the portal, for a customer.
      await tryExchange();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'The password could not be saved.');
    } finally { setSubmitting(false); }
  };

  const leaveFirstSignIn = () => {
    setFirstStep(null); setFirstCode(''); setFirstNote(null); setNewPassword(''); setNewConfirm(''); setError(null);
    firstSessionRef.current = null;
  };

  // A NEW CUSTOMER CREATES THEIR OWN ACCOUNT (owner, 2026-10-16), where the
  // site has switched it on (Settings › Customer Portal). Name and email ->
  // an emailed code -> the same "choose a password" step as a first sign-in
  // -> the portal. The server makes the account only once the code is right
  // (auth-supabase/server/self-signup.js).
  //   signUpOn    the site offers it
  //   signStep    null | 'form' | 'code'
  //   signDesk    { key, name } when the page was opened by a desk's own
  //               sign-up link (/?signup=KEY, 2026-10-28): the form opens at
  //               once, for that desk, and the account sees only that desk
  const [signUpOn, setSignUpOn] = useState(false);
  const [signStep, setSignStep] = useState(null);
  const [signName, setSignName] = useState('');
  const [signDesk, setSignDesk] = useState(null);
  useEffect(() => {
    if (!isSupabaseConfigured()) return undefined;
    let live = true;
    const deskKey = new URLSearchParams(window.location.search).get('signup');
    if (deskKey) {
      api.signUpEnabled(deskKey).then((r) => {
        if (!live) return;
        if (r?.enabled && r.desk) { setSignDesk(r.desk); setSignStep('form'); }
        else setError('This sign-up link is not open. Contact us to be added.');
      }).catch(() => {});
    } else {
      api.signUpEnabled().then((r) => { if (live) setSignUpOn(!!r?.enabled); }).catch(() => {});
    }
    return () => { live = false; };
  }, []);

  const sendSignUpCode = async (again = false) => {
    setError(null); setFirstNote(null);
    if (!signName.trim()) { setError('Type your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('That email address is not valid.'); return; }
    setSubmitting(true);
    try {
      const r = await api.signUpStart(signName.trim(), email.trim(), signDesk?.key || null);
      if (r?.state === 'exists') {
        // Already known: back to signing in — a first-timer is offered their code there.
        setSignStep(null);
        setFirstNote('This email already has an account. Sign in below.');
        return;
      }
      setSignStep('code');
      if (again) setFirstNote(r?.sent ? 'A new code is on its way.' : 'A code was sent less than a minute ago — check your inbox, or try again in a minute.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'The code could not be sent.');
    } finally { setSubmitting(false); }
  };

  const verifySignUpCode = async (e) => {
    e.preventDefault();
    setError(null); setFirstNote(null);
    const code = firstCode.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) { setError('Type the 6-digit code from the email — numbers only.'); return; }
    setSubmitting(true);
    try {
      firstSessionRef.current = await api.signUpVerify(email.trim(), code);
      setSignStep(null);
      setFirstStep('password');
    } catch (err) {
      if (err?.response?.data?.state === 'exists') { setSignStep(null); setFirstCode(''); }
      setError(err?.response?.data?.error || err?.message || 'That code did not work.');
    } finally { setSubmitting(false); }
  };

  const openSignUp = () => { setError(null); setFirstNote(null); setFirstCode(''); setResetSent(false); setSignStep('form'); };
  const leaveSignUp = () => { setSignStep(null); setFirstCode(''); setFirstNote(null); setError(null); };

  // Two-factor (TOTP) sub-flow. Only the browser-side Supabase path
  // uses this — the legacy server-login path never enters an MFA step.
  //   mfaStep: null      → normal email/password form
  //            'challenge'→ user already has a factor, enter a code
  //            'enroll'   → user is required but has no factor; scan QR
  //                          then enter the first code to verify+enrol
  const [mfaStep, setMfaStep] = useState(null);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaQrCode, setMfaQrCode] = useState(null);
  const [mfaSecret, setMfaSecret] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState(null);

  // 2FA STEP-UP mode. An already-logged-in aal1 user hit an admin action
  // that returned 403 MFA_REQUIRED; the api-client interceptor bounced
  // them here with ?mfa=stepup&returnUrl=<where they were>. We skip the
  // email/password form and go straight to challenge/enrol against the
  // live Supabase session, then send them back to returnUrl at aal2.
  // Computed synchronously (refs) like the invite/OAuth classifiers so
  // the render guard + auto-navigate effect can see it on the first render.
  const stepUpRef = useRef(null);
  const returnUrlRef = useRef('/');
  if (stepUpRef.current === null) {
    let isStepUp = false;
    try {
      const params = new URLSearchParams(window.location.search);
      isStepUp = params.get('mfa') === 'stepup';
      returnUrlRef.current = safeReturnUrl(params.get('returnUrl'));
    } catch (_) { /* URL parse failure → not step-up */ }
    stepUpRef.current = isStepUp;
  }
  // When the Supabase session is gone (expired) but the PWA JWT is still
  // valid, browser MFA can't run — fall back to the password form with an
  // explanatory banner instead of a dead-end spinner.
  const [stepUpNeedsLogin, setStepUpNeedsLogin] = useState(false);

  // Invite / password-reset link catcher. Supabase redirects the browser
  // back with the session token in the URL hash (#access_token=...&
  // type=invite|recovery). When the configured redirect target isn't
  // honored, that redirect falls back to the Site URL (this sign-in page)
  // instead of /auth/reset-password, and the invited user is stranded
  // here. Rather than depend on per-client Supabase redirect config being
  // perfect, detect the token on the sign-in page and forward to the
  // set-password page, carrying the hash/query verbatim.
  //
  // Computed synchronously (refs, not state) so the logged-in redirect
  // below can guard on them in the SAME render — otherwise a stale session
  // could navigate to /structure before the forward fires.
  //
  // Two distinct URL shapes arrive back on this page and must NOT be
  // conflated:
  //   • Invite / password-recovery links → forward to the set-password
  //     page (carry the hash/query verbatim).
  //   • Google OAuth completion → let supabase-js consume the returned
  //     token here, then exchange for the app JWT (handled by the effect
  //     placed after tryExchange).
  // A bare `#access_token=` / `?code=` matches BOTH a recovery link and an
  // OAuth return, so invite/recovery is detected by its explicit
  // `type=invite|recovery` (or `token_hash=`) marker only, and OAuth is
  // whatever's left.
  const isInviteRecoveryRef = useRef(null);
  const isOAuthReturnRef = useRef(null);
  if (isInviteRecoveryRef.current === null) {
    const hash = window.location.hash || '';
    const query = window.location.search || '';
    const isInviteRecovery =
      /[#&?]type=(invite|recovery)/.test(hash + query) ||
      /[?&]token_hash=/.test(query);
    isInviteRecoveryRef.current = isInviteRecovery;
    isOAuthReturnRef.current =
      !isInviteRecovery &&
      (/[#&]access_token=/.test(hash) || /[?&]code=/.test(query));
  }

  useEffect(() => {
    if (isInviteRecoveryRef.current) {
      navigate(`/auth/reset-password${window.location.search}${window.location.hash}`, { replace: true });
    }
  }, [navigate]);

  // GoTrue ERROR redirects (e.g. a re-clicked, already-consumed invite
  // link → #error=access_denied&error_code=otp_expired) carry no type=/
  // token, so neither classifier above matches — previously they were
  // silently dropped and the user just saw a bare sign-in form with no
  // explanation. Surface the message in the login error slot and scrub
  // the fragment so a refresh doesn't re-show it. Also covers failed
  // OAuth returns, which arrive in the same shape.
  useEffect(() => {
    if (isInviteRecoveryRef.current || isOAuthReturnRef.current) return;
    const hash = window.location.hash || '';
    const query = window.location.search || '';
    const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const q = new URLSearchParams(query);
    const errCode = h.get('error') || q.get('error') || h.get('error_code') || q.get('error_code');
    if (!errCode) return;
    const desc = h.get('error_description') || q.get('error_description');
    setError(
      `${desc || 'This sign-in link is invalid or has expired.'} ` +
      'If you followed an invite or password-reset email, the link may have ' +
      'already been used — ask your administrator for a new invite, or use ' +
      '"Forgot password?" below.'
    );
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  useEffect(() => {
    // Step-up mode deliberately runs WITH an active user (they're already
    // logged in at aal1) — don't bounce them to Home before they
    // can elevate.
    if (!isInviteRecoveryRef.current && !isOAuthReturnRef.current && !stepUpRef.current && !loading && user) {
      navigate(takeLandingAfterSignIn(), { replace: true });
    }
  }, [user, loading, navigate]);

  // Start the Google OAuth flow. supabase.auth.signInWithOAuth redirects
  // the whole browser to Google; on return the OAuth-completion effect
  // below picks up the session and runs the same exchange (+ 2FA step-up)
  // as the password path. Only meaningful when Supabase is configured.
  const handleGoogleLogin = async () => {
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (oauthError) throw oauthError;
      // Browser now redirects to Google — nothing else to do here.
    } catch (err) {
      setError(err?.message || 'Could not start Google sign-in');
    }
  };

  // Exchange the current Supabase session for our app JWT and finish
  // login. The SERVER's requires_mfa flag is the single control: it either
  // mints the app JWT (not required — even if the account has a dormant
  // factor from a since-disabled requirement) or 403 MFA_REQUIRED. On that
  // 403 we step up: challenge an existing verified factor, else enrol a
  // new one inline. Doing 2FA reactively on the 403 (rather than pre-empting
  // by factor existence) is what makes disabling the toggle actually stop
  // the challenge. Browser-side path only.
  // Move into the challenge (existing verified factor) or enrol (none
  // yet) sub-view based on the account's current Supabase factors. Shared
  // by the reactive 403 path (tryExchange) and the proactive step-up path.
  const beginMfaFromFactors = async () => {
    const supabase = getSupabaseClient();
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const verified = (factors?.totp || []).find((f) => f.status === 'verified');
    if (verified) {
      // Already set up — challenge for the current code.
      setMfaFactorId(verified.id);
      setMfaCode('');
      setMfaError(null);
      setMfaStep('challenge');
      return;
    }
    // Not set up — force inline enrolment. First clear any UNVERIFIED
    // factors left by an abandoned attempt (e.g. a closed tab mid-enrol):
    // GoTrue rejects a second enroll with a friendly-name conflict, so a
    // stale factor would dead-end the retry. listFactors().totp is
    // VERIFIED-only (auth-js filters it), so unverified leftovers live in
    // .all — iterate that, not .totp (the old .totp loop was a no-op).
    // Runs in BOTH the reactive 403 path and step-up init.
    for (const f of (factors?.all || [])) {
      if (f.factor_type === 'totp' && f.status !== 'verified') {
        await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {});
      }
    }
    const { data: enroll, error: enrollError } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (enrollError) throw enrollError;
    setMfaFactorId(enroll.id);
    setMfaQrCode(enroll.totp?.qr_code || null);
    setMfaSecret(enroll.totp?.secret || null);
    setMfaCode('');
    setMfaError(null);
    setMfaStep('enroll');
  };

  // Store the freshly-minted app JWT and land the user. In step-up mode
  // that means a FULL reload back to where they were (returnUrl) so every
  // component re-fetches under the new aal2 token; the normal login path
  // does an in-app navigate to Home (or the link that sent them here).
  const finishLogin = async (appToken) => {
    await login(appToken);
    if (stepUpRef.current) {
      window.location.href = returnUrlRef.current;
    } else {
      navigate(takeLandingAfterSignIn(), { replace: true });
    }
  };

  const tryExchange = async () => {
    const supabase = getSupabaseClient();
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) throw new Error('Session expired — please sign in again');
    try {
      const data = await api.authExchange(accessToken);
      if (!data?.token) throw new Error('No token returned');
      await finishLogin(data.token);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.code === 'MFA_REQUIRED') {
        await beginMfaFromFactors();
        return;
      }
      throw err;
    }
  };

  // Google OAuth completion. On the redirect back from Google the URL
  // carries the session (#access_token=… or ?code=…); supabase-js consumes
  // it on init (detectSessionInUrl). We read the resulting session and run
  // the SAME exchange as the password path — tryExchange handles success,
  // navigation and the 2FA step-up. Guarded by a ref so StrictMode /
  // re-render can't double-run it.
  const oauthHandledRef = useRef(false);
  useEffect(() => {
    if (!isOAuthReturnRef.current || oauthHandledRef.current) return;
    oauthHandledRef.current = true;
    (async () => {
      setSubmitting(true);
      try {
        const supabase = getSupabaseClient();
        let { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          // supabase-js may not have finished parsing the returned token
          // on the first tick — wait briefly and retry once.
          await new Promise((r) => setTimeout(r, 400));
          ({ data: sessionData } = await supabase.auth.getSession());
        }
        // Scrub the OAuth artifacts from the URL so a refresh can't re-run.
        window.history.replaceState(null, '', window.location.pathname);
        if (sessionData?.session) {
          await tryExchange();
        } else {
          setError('Google sign-in did not complete. Please try again.');
        }
      } catch (err) {
        const raw = err?.response?.data?.error || err?.message || 'Google sign-in failed';
        if (/no matching stride user/i.test(raw)) {
          setError('This Google account is not registered. Ask your administrator to add you.');
          // Drop the orphan Supabase session so it can't linger.
          getSupabaseClient().auth.signOut().catch(() => {});
        } else {
          setError(raw);
        }
      } finally {
        setSubmitting(false);
      }
    })();
    // Runs once on mount; isOAuthReturnRef is fixed for this page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step-up entry. On mount in ?mfa=stepup mode, skip the password form
  // and drive straight to challenge/enrol against the live Supabase
  // session. Guarded by a ref so StrictMode / re-render can't double-run.
  const stepUpHandledRef = useRef(false);
  useEffect(() => {
    if (!stepUpRef.current || stepUpHandledRef.current) return;
    stepUpHandledRef.current = true;
    (async () => {
      // No frontend Supabase (legacy server-login deployments): browser
      // MFA can't run at all — fall back to the form + banner.
      if (!isSupabaseConfigured()) { setStepUpNeedsLogin(true); return; }
      setSubmitting(true);
      try {
        const supabase = getSupabaseClient();
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session) {
          // PWA JWT still valid but the Supabase session lapsed — they
          // must sign in again to get a fresh Supabase session to enrol
          // against. Degenerate, but not a dead-end.
          setStepUpNeedsLogin(true);
          return;
        }
        await beginMfaFromFactors();
      } catch (err) {
        setStepUpNeedsLogin(true);
        setError(err?.message || 'Could not start two-factor setup — sign in again to continue.');
      } finally {
        setSubmitting(false);
      }
    })();
    // Runs once on mount; stepUpRef is fixed for this page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSupabaseLogin = async (e) => {
    e.preventDefault();
    if (first) { await sendFirstCode(false); return; }
    setError(null);
    setSubmitting(true);
    try {
      if (isSupabaseConfigured()) {
        // Browser-side flow: sign in against Supabase Auth directly,
        // then exchange the Supabase access token for our app JWT.
        // Only active when VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY
        // are set on the frontend build.
        const supabase = getSupabaseClient();
        const { data: signIn, error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (signInError || !signIn?.session?.access_token) {
          // Supabase says "Invalid login credentials" — keep the same
          // message the server-side login path returns.
          const raw = signInError?.message || 'Login failed';
          throw new Error(/invalid login credentials/i.test(raw) ? 'Invalid email or password' : raw);
        }
        // Exchange first; requires_mfa (server) decides whether 2FA runs.
        await tryExchange();
      } else {
        // Legacy server-side login for clients without the frontend
        // Supabase env vars — unchanged behaviour.
        const data = await api.supabaseLogin(email.trim(), password);
        if (!data?.token) throw new Error('No token returned');
        await login(data.token);
        navigate(takeLandingAfterSignIn(), { replace: true });
      }
    } catch (err) {
      // Legacy server-login path can't run the browser TOTP challenge, so a
      // required account 403s MFA_REQUIRED with no way forward here. Give a
      // specific message rather than the raw server line. (The api-client
      // interceptor deliberately excludes /api/auth/supabase/login from its
      // step-up redirect so this message can show.)
      if (err?.response?.status === 403 && err?.response?.data?.code === 'MFA_REQUIRED') {
        setError("Two-factor authentication is required for this account, but this device isn't set up for browser-based sign-in. Contact your administrator to complete two-factor setup.");
      } else {
        const msg = err?.response?.data?.error || err?.message || 'Login failed';
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Verify a 6-digit TOTP code, then exchange. Shared by the challenge
  // (existing factor) and enrol (new factor) sub-views — challengeAndVerify
  // both verifies the code and, for a freshly-enrolled factor, marks it
  // verified, so the session becomes aal2 either way.
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMfaError(null);
    setSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: mfaCode.trim(),
      });
      if (verifyError) {
        setMfaError('Invalid code — try again');
        return;
      }
      await tryExchange();
    } catch (err) {
      setMfaError(err?.response?.data?.error || err?.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const backToSignIn = () => {
    setMfaStep(null);
    setMfaFactorId(null);
    setMfaQrCode(null);
    setMfaSecret(null);
    setMfaCode('');
    setMfaError(null);
    // Cancelling a step-up: the user stays aal1. Deliberately land on
    // Home (non-admin-gated) rather than returnUrl — returning to
    // returnUrl would either re-trigger step-up immediately if it
    // auto-fires admin calls (a no-escape ping-pong) or, when returnUrl
    // is '/', leave THIS same Home instance mounted on a permanent
    // "Preparing…" spinner (refs persist, the init effect won't re-run).
    // Home (/home) unmounts this sign-in page, drops the ?mfa=stepup query, and works
    // fine at aal1; the next admin action re-triggers step-up cleanly.
    if (stepUpRef.current) {
      navigate(HOME_URL, { replace: true });
    }
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setError('Enter your email above first, then click "Forgot password?"');
      return;
    }
    setError(null);
    setResetSent(false);
    try {
      await api.supabaseRequestPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Reset request failed');
    }
  };

  // In step-up mode we render the card (MFA UI) even with an active user —
  // they're logged in at aal1 and need to elevate. Otherwise the usual
  // "logged in → spinner while the redirect effect runs" applies.
  if (loading || (user && !stepUpRef.current)) {
    return (
      <div className="home-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="landing">
      <CircuitBackground />

      <div className="landing-card">
        <div className="landing-brand">
          <SignInLogo />
        </div>

        <p className="landing-welcome">
          {stepUpRef.current ? 'Two-factor required' : (first || firstStep || signStep ? 'Welcome' : 'Welcome back')}
        </p>
        <p className="landing-subtitle">
          {stepUpRef.current
            ? 'Admin actions need two-factor authentication'
            : signStep ? 'Create your account' : `Sign in to your ${BRAND_SUBTITLE} workspace`}
        </p>

        {stepUpRef.current && !mfaStep && !stepUpNeedsLogin ? (
          <div className="landing-login-form">
            <p className="landing-mfa-lead">Preparing two-factor authentication…</p>
            <div className="spinner" />
          </div>
        ) : mfaStep ? (
          <form className="landing-login-form" onSubmit={handleMfaVerify}>
            {mfaStep === 'enroll' ? (
              <>
                <p className="landing-mfa-lead">
                  Two-factor authentication is required for your account.
                  Scan this QR code with an authenticator app (Google
                  Authenticator, 1Password, Authy…), then enter the 6-digit
                  code it shows.
                </p>
                {mfaQrCode && (
                  <img className="landing-qr" src={mfaQrCode} alt="Scan with your authenticator app" />
                )}
                {mfaSecret && (
                  <div className="landing-secret">
                    <span className="landing-secret-label">Can't scan? Enter this key manually:</span>
                    <code className="landing-secret-code">{mfaSecret}</code>
                  </div>
                )}
              </>
            ) : (
              <p className="landing-mfa-lead">
                Enter the 6-digit code from your authenticator app to finish
                signing in.
              </p>
            )}
            <input
              type="text"
              className="landing-login-input landing-mfa-code"
              placeholder="000000"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              disabled={submitting}
            />
            {mfaError && <div className="landing-login-error">{mfaError}</div>}
            <button type="submit" className="landing-cta" disabled={submitting || mfaCode.length !== 6}>
              {submitting ? 'Verifying…' : (mfaStep === 'enroll' ? 'Verify & enable' : 'Verify')}
            </button>
            <button
              type="button"
              className="landing-login-link"
              onClick={backToSignIn}
              disabled={submitting}
            >
              Back to sign in
            </button>
          </form>
        ) : signStep === 'form' ? (
          <form className="landing-login-form" onSubmit={(e) => { e.preventDefault(); sendSignUpCode(false); }}>
            {signDesk && (
              <div className="landing-login-info">Create your account for <b>{signDesk.name}</b>.</div>
            )}
            <div className="landing-field">
              <label className="landing-field-label" htmlFor="landing-signup-name">Your name</label>
              <input
                id="landing-signup-name"
                type="text"
                className="landing-login-input"
                placeholder="First and last name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                autoComplete="name"
                maxLength={120}
                autoFocus
                required
                disabled={submitting}
              />
            </div>
            <div className="landing-field">
              <label className="landing-field-label" htmlFor="landing-signup-email">Email</label>
              <input
                id="landing-signup-email"
                type="email"
                className="landing-login-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                disabled={submitting}
              />
            </div>
            <div className="landing-login-info">We will email you a code to check the address, then you choose a password.</div>
            {error && <div className="landing-login-error">{error}</div>}
            <button type="submit" className="landing-cta" disabled={submitting}>
              {submitting ? 'Sending a code…' : 'Send me a code'}
            </button>
            <button type="button" className="landing-login-link" onClick={leaveSignUp} disabled={submitting}>
              Back to sign in
            </button>
          </form>
        ) : signStep === 'code' ? (
          <form className="landing-login-form" onSubmit={verifySignUpCode}>
            <div className="landing-login-info">
              We have emailed a 6-digit code to <b>{email.trim()}</b>. Type it here to create your account.
            </div>
            <input
              type="text"
              className="landing-login-input landing-mfa-code"
              placeholder="000000"
              value={firstCode}
              onChange={(e) => setFirstCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Code from the email"
              autoFocus
              required
              disabled={submitting}
            />
            {error && <div className="landing-login-error">{error}</div>}
            {firstNote && <div className="landing-login-info">{firstNote}</div>}
            <button type="submit" className="landing-cta" disabled={submitting || !firstCode}>
              {submitting ? 'Checking…' : 'Continue'}
            </button>
            <button type="button" className="landing-login-link" onClick={() => sendSignUpCode(true)} disabled={submitting}>
              No email? Send a new code
            </button>
            <button type="button" className="landing-login-link" onClick={leaveSignUp} disabled={submitting}>
              Back to sign in
            </button>
          </form>
        ) : firstStep === 'code' ? (
          <form className="landing-login-form" onSubmit={verifyFirstCode}>
            <div className="landing-login-info">
              We have emailed a 6-digit code to <b>{email.trim()}</b>. Type it here to set your password.
            </div>
            <input
              type="text"
              className="landing-login-input landing-mfa-code"
              placeholder="000000"
              value={firstCode}
              onChange={(e) => setFirstCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Code from the email"
              autoFocus
              required
              disabled={submitting}
            />
            {error && <div className="landing-login-error">{error}</div>}
            {firstNote && <div className="landing-login-info">{firstNote}</div>}
            <button type="submit" className="landing-cta" disabled={submitting || !firstCode}>
              {submitting ? 'Checking…' : 'Continue'}
            </button>
            <button type="button" className="landing-login-link" onClick={() => sendFirstCode(true)} disabled={submitting}>
              No email? Send a new code
            </button>
            <button type="button" className="landing-login-link" onClick={leaveFirstSignIn} disabled={submitting}>
              Back to sign in
            </button>
          </form>
        ) : firstStep === 'password' ? (
          <form className="landing-login-form" onSubmit={saveFirstPassword}>
            <div className="landing-login-info">Choose a password for <b>{email.trim()}</b>. You will use it to sign in from now on.</div>
            <div className="landing-field">
              <label className="landing-field-label" htmlFor="landing-new-password">New password</label>
              <input
                id="landing-new-password"
                type={showPassword ? 'text' : 'password'}
                className="landing-login-input"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
                disabled={submitting}
              />
            </div>
            <div className="landing-field">
              <label className="landing-field-label" htmlFor="landing-new-confirm">Confirm password</label>
              <input
                id="landing-new-confirm"
                type={showPassword ? 'text' : 'password'}
                className="landing-login-input"
                value={newConfirm}
                onChange={(e) => setNewConfirm(e.target.value)}
                autoComplete="new-password"
                required
                disabled={submitting}
              />
            </div>
            <button type="button" className="landing-login-link" onClick={() => setShowPassword((v) => !v)} disabled={submitting}>
              {showPassword ? 'Hide password' : 'Show password'}
            </button>
            {error && <div className="landing-login-error">{error}</div>}
            <button type="submit" className="landing-cta" disabled={submitting}>
              {submitting ? 'Saving…' : 'Set password & continue'}
            </button>
          </form>
        ) : (
        <form className="landing-login-form" onSubmit={handleSupabaseLogin}>
          {stepUpNeedsLogin && (
            <div className="landing-login-info">
              {isSupabaseConfigured()
                ? 'Your session needs a fresh sign-in to enable two-factor authentication for admin actions.'
                : "This device isn't set up for browser-based two-factor authentication. Contact your administrator to complete two-factor setup for admin actions."}
            </div>
          )}
          <div className="landing-field">
            <label className="landing-field-label" htmlFor="landing-email">Email</label>
            <input
              id="landing-email"
              type="email"
              className="landing-login-input"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
              disabled={submitting}
            />
          </div>
          {first ? (
            <div className="landing-login-info">
              Welcome — this is your first sign-in. Press Continue and we will email you a code to set your password.
            </div>
          ) : (
          <div className="landing-field">
            <label className="landing-field-label" htmlFor="landing-password">Password</label>
            <div className="landing-password-wrap">
              <input
                id="landing-password"
                type={showPassword ? 'text' : 'password'}
                className="landing-login-input landing-password-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="landing-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide password' : 'Show password'}
                disabled={submitting}
              >
                {showPassword ? (
                  /* eye-off */
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  /* eye */
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          )}
          {error && <div className="landing-login-error">{error}</div>}
          {firstNote && !firstStep && <div className="landing-login-info">{firstNote}</div>}
          {resetSent && (
            <div className="landing-login-info">
              If that email is registered, a password-reset link has been sent. Check your inbox.
            </div>
          )}
          <button type="submit" className="landing-cta" disabled={submitting}>
            {submitting ? (first ? 'Sending a code…' : 'Signing in…') : (first ? 'Continue' : 'Sign in')}
          </button>
          {!first && (
          <button
            type="button"
            className="landing-login-link"
            onClick={handlePasswordReset}
            disabled={submitting}
          >
            Forgot password?
          </button>
          )}
          {signUpOn && !first && !stepUpRef.current && (
            <button type="button" className="landing-login-link" onClick={openSignUp} disabled={submitting}>
              New here? Create an account
            </button>
          )}
        </form>
        )}

        {!mfaStep && !firstStep && !signStep && !first && isSupabaseConfigured() && (
          <>
            <div className="landing-login-divider">or</div>
            <button
              type="button"
              className="landing-google-btn"
              onClick={handleGoogleLogin}
              disabled={submitting}
            >
              <svg className="landing-google-icon" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
              </svg>
              Sign in with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Home;
